import { useState, useEffect, useCallback, useRef } from "react";
import {
  healthCheckService,
  type HealthCheckResponse,
} from "../services/HealthCheckService";
export interface UseHealthCheckOptions {
  /** Enable automatic periodic health checks */
  autoRefresh?: boolean;
  /** Interval in milliseconds for automatic refresh (default: 30000ms / 30 seconds) */
  refreshInterval?: number;
  /** Initial check on mount (default: false) */
  checkOnMount?: boolean;
}
export interface UseHealthCheckReturn {
  /** Current health data */
  healthData: HealthCheckResponse | null;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: string | null;
  /** Manually trigger a health check */
  refetch: () => Promise<void>;
  /** Start automatic health checks */
  startAutoRefresh: () => void;
  /** Stop automatic health checks */
  stopAutoRefresh: () => void;
  /** Check if auto refresh is active */
  isAutoRefreshActive: boolean;
  /** Get overall system status */
  overallStatus: "healthy" | "degraded" | "unhealthy" | "unknown";
  /** Get count of healthy services */
  healthyServicesCount: number;
  /** Get total services count */
  totalServicesCount: number;
}
/**
 * Custom hook for managing health check functionality
 *
 * @example
 * ```tsx
 * const { healthData, isLoading, refetch, overallStatus } = useHealthCheck({
 *   autoRefresh: true,
 *   refreshInterval: 60000, // Check every minute
 *   checkOnMount: true
 * });
 * ```
 */
export const useHealthCheck = (
  options: UseHealthCheckOptions = {},
): UseHealthCheckReturn => {
  const {
    autoRefresh = false,
    refreshInterval = 30000,
    checkOnMount = false,
  } = options;
  const [healthData, setHealthData] = useState<HealthCheckResponse | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAutoRefreshActive, setIsAutoRefreshActive] = useState(autoRefresh);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isActiveRef = useRef(true);
  const fetchHealthStatus = useCallback(async (): Promise<void> => {
    if (!isActiveRef.current) {
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const data = await healthCheckService.checkAllServices();
      if (isActiveRef.current) {
        setHealthData(data);
      }
    } catch (err) {
      if (isActiveRef.current) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to fetch health status";
        setError(errorMessage);
      }
    } finally {
      if (isActiveRef.current) {
        setIsLoading(false);
      }
    }
  }, []);
  const startAutoRefresh = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    setIsAutoRefreshActive(true);
    intervalRef.current = setInterval(() => {
      void fetchHealthStatus();
    }, refreshInterval);
  }, [fetchHealthStatus, refreshInterval]);
  const stopAutoRefresh = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsAutoRefreshActive(false);
  }, []);
  // Initial check on mount if requested
  useEffect(() => {
    if (checkOnMount) {
      void fetchHealthStatus();
    }
  }, [checkOnMount, fetchHealthStatus]);
  // Setup auto refresh
  useEffect(() => {
    if (autoRefresh) {
      startAutoRefresh();
    }
    return () => {
      stopAutoRefresh();
    };
  }, [autoRefresh, startAutoRefresh, stopAutoRefresh]);
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isActiveRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);
  // Computed values
  const overallStatus: "healthy" | "degraded" | "unhealthy" | "unknown" =
    healthData?.overallStatus ?? "unknown";
  const healthyServicesCount =
    healthData?.services.filter((s) => s.status === "healthy").length ?? 0;
  const totalServicesCount = healthData?.services.length ?? 0;
  return {
    healthData,
    isLoading,
    error,
    refetch: fetchHealthStatus,
    startAutoRefresh,
    stopAutoRefresh,
    isAutoRefreshActive,
    overallStatus,
    healthyServicesCount,
    totalServicesCount,
  };
};
